import React, { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BrowserRouter } from 'react-router-dom';
import './App.css';
import './index.css'; // <-- Standard for CRA/Vite + Tailwind

import App from './App';

interface ErrorUIStyles extends React.CSSProperties {
  minHeight: string;
  display: string;
  alignItems: string;
  justifyContent: string;
  padding: string;
  backgroundColor: string;
  fontFamily: string;
}

interface CardStyles extends React.CSSProperties {
  maxWidth: string;
  width: string;
  background: string;
  borderRadius: string;
  boxShadow: string;
  padding: string;
  textAlign: 'center';
}

interface ButtonStyles extends React.CSSProperties {
  width: string;
  padding: string;
  backgroundColor: string;
  color: string;
  border: string;
  borderRadius: string;
  cursor: string;
  fontSize: string;
}

const AppWrapper: React.FC = () => {
  useEffect(() => {
    const hideSpinner = (): void => {
      const spinner = document.querySelector('.loading-spinner') as HTMLElement;
      if (spinner) {
        spinner.style.opacity = '0';
        setTimeout(() => {
          spinner.style.display = 'none';
        }, 300);
      }
    };

    // Hide spinner when React loads
    hideSpinner();
    
    // Safety timeout in case React doesn't load properly
    const timeoutId = setTimeout(() => {
      hideSpinner();
      // If React hasn't loaded after 30 seconds, show error message
      const root = document.getElementById('root');
      if (root && root.children.length === 0) {
        const container = document.createElement('div');
        const containerStyles: ErrorUIStyles = {
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          backgroundColor: '#f9fafb',
          fontFamily: 'system-ui, sans-serif',
        };
        Object.assign(container.style, containerStyles);

        const card = document.createElement('div');
        const cardStyles: CardStyles = {
          maxWidth: '28rem',
          width: '100%',
          background: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          padding: '1.5rem',
          textAlign: 'center',
        };
        Object.assign(card.style, cardStyles);

        const heading = document.createElement('h2');
        heading.style.color = '#ef4444';
        heading.style.marginBottom = '1rem';
        heading.style.fontSize = '1.25rem';
        heading.style.fontWeight = '600';
        heading.textContent = 'Error de Carga';

        const message = document.createElement('p');
        message.style.color = '#6b7280';
        message.style.marginBottom = '1.5rem';
        message.style.lineHeight = '1.6';
        message.textContent = 'La aplicación no pudo cargar correctamente. Por favor, verifica tu conexión a internet e intenta nuevamente.';

        const button = document.createElement('button');
        const buttonStyles: ButtonStyles = {
          width: '100%',
          padding: '0.5rem 1rem',
          backgroundColor: '#ea580c',
          color: 'white',
          border: 'none',
          borderRadius: '0.5rem',
          cursor: 'pointer',
          fontSize: '1rem',
        };
        Object.assign(button.style, buttonStyles);
        
        button.textContent = 'Recargar Página';
        button.onclick = (): void => window.location.reload();
        button.onmouseover = (): void => {
          button.style.backgroundColor = '#dc2626';
        };
        button.onmouseout = (): void => {
          button.style.backgroundColor = '#ea580c';
        };

        card.appendChild(heading);
        card.appendChild(message);
        card.appendChild(button);
        container.appendChild(card);
        root.appendChild(container);
      }
    }, 30000);

    const onLoad = (): void => {
      if (window.performance && window.performance.mark) {
        window.performance.mark('app-loaded');
      }
    };
    window.addEventListener('load', onLoad);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('load', onLoad);
    };
  }, []);

  return <App />;
};

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

const root = createRoot(rootElement);
root.render(
  <StrictMode>
    <BrowserRouter>
      <DndProvider backend={HTML5Backend}>
        <AppWrapper />
      </DndProvider>
    </BrowserRouter>
  </StrictMode>
);